import { useEffect, useState } from 'react';
import { Activity, AlertTriangle, RefreshCw } from 'lucide-react';
import { AnalysisJournal } from './components/AnalysisJournal';
import { DataHealth } from './components/DataHealth';
import { HoldingsTable } from './components/HoldingsTable';
import { LoginGate } from './components/LoginGate';
import { PortfolioHeader } from './components/PortfolioHeader';
import { RiskBands } from './components/RiskBands';

type SessionState = 'checking' | 'authenticated' | 'unauthenticated';
type OverviewState = 'idle' | 'loading' | 'ready' | 'empty' | 'error';

export type PortfolioHolding = {
  symbol: string;
  name: string;
  bucket: string;
  role: string;
  quantity: number;
  averagePrice: number | null;
  latestQuotePrice: number | null;
  quoteProvider: string | null;
  quoteTime: string | null;
  quoteRetrievedAt: string | null;
  staleAfterSeconds: number | null;
  brokerMarketValue: number | null;
};

export type PortfolioOverview = {
  estimatedNav: number;
  cashValue: number;
  positionValue: number;
  holdings: PortfolioHolding[];
};

export type OverviewResponse = {
  ok: boolean;
  overview: PortfolioOverview;
  error?: string;
};

export function App() {
  const [sessionState, setSessionState] = useState<SessionState>('checking');
  const [overviewState, setOverviewState] = useState<OverviewState>('idle');
  const [overview, setOverview] = useState<PortfolioOverview | null>(null);
  const [overviewError, setOverviewError] = useState<string | null>(null);
  const [fetchedAt, setFetchedAt] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    async function checkSession() {
      try {
        const response = await fetch('/api/auth/session', { credentials: 'same-origin' });
        const body = (await response.json()) as { ok?: boolean };
        if (active) setSessionState(response.ok && body.ok === true ? 'authenticated' : 'unauthenticated');
      } catch {
        if (active) setSessionState('unauthenticated');
      }
    }

    void checkSession();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (sessionState !== 'authenticated') return;

    let active = true;

    async function fetchOverview() {
      setOverviewState('loading');
      setOverviewError(null);

      try {
        const response = await fetch('/api/portfolio/overview', { credentials: 'same-origin' });
        const body = (await response.json()) as Partial<OverviewResponse> & { ok?: boolean; error?: string };

        if (!active) return;

        if (!response.ok || body.ok !== true) {
          setOverview(null);
          setFetchedAt(null);
          setOverviewError(body.error ?? 'Portfolio overview is unavailable.');
          setOverviewState('error');
          return;
        }

        if (!body.overview) {
          setOverview(null);
          setFetchedAt(new Date().toISOString());
          setOverviewState('empty');
          return;
        }

        setOverview(body.overview);
        setFetchedAt(new Date().toISOString());
        setOverviewState('ready');
      } catch {
        if (!active) return;
        setOverview(null);
        setFetchedAt(null);
        setOverviewError('Portfolio overview could not be loaded.');
        setOverviewState('error');
      }
    }

    void fetchOverview();

    return () => {
      active = false;
    };
  }, [sessionState]);

  if (sessionState === 'checking') {
    return (
      <main className="status-shell" aria-live="polite">
        <div className="status-panel">
          <RefreshCw size={20} aria-hidden="true" />
          <p>Checking session...</p>
        </div>
      </main>
    );
  }

  if (sessionState === 'unauthenticated') {
    return <LoginGate onAuthenticated={() => setSessionState('authenticated')} />;
  }

  return (
    <main className="app-shell" aria-live={overviewState === 'loading' ? 'polite' : 'off'}>
      {overviewState === 'loading' || overviewState === 'idle' ? (
        <DashboardShellState icon={<RefreshCw size={20} aria-hidden="true" />} title="Loading portfolio overview" />
      ) : null}

      {overviewState === 'error' ? (
        <DashboardShellState
          icon={<AlertTriangle size={20} aria-hidden="true" />}
          title="Portfolio overview unavailable"
          detail={overviewError ?? 'Try again after the data API is healthy.'}
          tone="error"
        />
      ) : null}

      {overviewState === 'empty' ? (
        <DashboardShellState
          icon={<Activity size={20} aria-hidden="true" />}
          title="No holdings available yet"
          detail="The authenticated account loaded, but the portfolio overview does not contain positions."
        />
      ) : null}

      {overviewState === 'ready' && overview ? <Dashboard overview={overview} fetchedAt={fetchedAt ?? undefined} /> : null}
    </main>
  );
}

type DashboardProps = {
  overview: PortfolioOverview;
  fetchedAt?: string;
};

export function Dashboard({ overview, fetchedAt }: DashboardProps) {
  const observedAt = fetchedAt ?? new Date().toISOString();

  return (
    <div className="dashboard">
      <PortfolioHeader overview={overview} fetchedAt={observedAt} />

      <section className="dashboard-grid" aria-label="Portfolio dashboard">
        <HoldingsTable overview={overview} fetchedAt={observedAt} />
        <RiskBands overview={overview} />
        <DataHealth overview={overview} fetchedAt={observedAt} />
        <AnalysisJournal />
      </section>
    </div>
  );
}

type DashboardShellStateProps = {
  icon: JSX.Element;
  title: string;
  detail?: string;
  tone?: 'neutral' | 'error';
};

function DashboardShellState({ icon, title, detail, tone = 'neutral' }: DashboardShellStateProps) {
  return (
    <section className={`dashboard-state ${tone}`} aria-label={title}>
      <div className="dashboard-state-icon">{icon}</div>
      <div>
        <h1>{title}</h1>
        {detail ? <p>{detail}</p> : null}
      </div>
    </section>
  );
}
